import styled from "styled-components/macro";
import { colors } from "src/theme/colors";
import { line, space, text } from "src/theme/ui";

/**
 * The balance panel beside the deposit form.
 *
 * It was three readings stacked in a periwinkle column — "Current Amount",
 * "Deposit Amount", "New Balance" — each a half-opacity black label over a
 * bold figure, two of them in accent blue on an accent-tinted ground. Two
 * things wrong with that.
 *
 * The panel was ACCENT ON ACCENT: on this tenant primary[10] is a real brand
 * tint rather than a neutral, and putting accent-coloured figures on it left
 * the column with no ink in it at all and nothing for the accent to mean.
 *
 * And it was hiding the only interesting thing about itself. Those three
 * numbers are not three facts, they are one SUM — the balance you have, the
 * amount you are adding, and what that comes to — and the third is the first
 * two added up. Printed as a list of equals, the reader has to notice the
 * arithmetic; printed as arithmetic, with the addend signed and a rule above
 * the total, they can see it.
 */
export const Container = styled.div`
  display: flex;
  flex-direction: column;
  padding: ${space.lg}px;
`;

export const Title = styled.div`
  ${text.label}
  margin-bottom: ${space.lg}px;
  font-size: 10.5px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${colors.gray[500]};
`;

/* Label left, figure right, so the figures make a column the eye can add up.
   Stacked, each label sat above its own figure and the three numbers never
   lined up with each other. */
export const Item = styled.div<{ $total?: boolean }>`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: ${space.md}px;
  padding: 9px 0;

  ${({ $total }) =>
    $total
      ? `
        margin-top: 6px;
        padding-top: 14px;
        border-top: 1px solid ${line.hairline};
      `
      : ""}
`;

export const SubTitle = styled.span`
  font-size: 12.5px;
  color: ${colors.gray[600]};
`;

export const ItemNote = styled.span`
  font-size: 12px;
  line-height: 1.5;
  color: ${colors.gray[500]};
`;

export const SubValue = styled.span<{ $tone?: string; $total?: boolean }>`
  ${text.numeric}
  font-size: ${({ $total }) => ($total ? "20px" : "15px")};
  font-weight: 600;
  letter-spacing: -0.01em;
  white-space: nowrap;
  color: ${({ $tone }) => $tone ?? colors.gray[900]};
`;
